import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetOne } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Link,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GetAppIcon from '@mui/icons-material/GetApp';
import ResourceBreadcrumbs from '../components/ResourceBreadcrumbs';
import { baseUrl } from '../customDataProvider';
import { downloadFileAuthenticated } from '../utils/downloadFile';

const getAccessToken = () => localStorage.getItem('accessToken') || '';

const handleDownloadPdf = async (formId: number, pdfFileId?: number) => {
  try {
    // Use the completed-forms endpoints which handle authentication properly
    const endpoint = pdfFileId
      ? `/completed-forms/${formId}/pdf`  // Stored PDF (now streams directly)
      : `/completed-forms/${formId}/make-pdf`;  // Generate PDF on-demand
    
    await downloadFileAuthenticated(endpoint, `completed-form-${formId}.pdf`);
  } catch (error) {
    console.error('PDF download failed:', error);
    // Show user-friendly error message
    alert('Failed to download PDF. Please try again or contact support.');
  }
};

// Component to show image thumbnails with click-to-expand functionality
const ImageThumbnail: React.FC<{
  fileId: number;
  thumbnail?: string;
  mimeType?: string;
  originalName?: string;
  onClick: () => void;
}> = ({ fileId, thumbnail, mimeType, originalName, onClick }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    
    const loadImage = async () => {
      try {
        // Load the original full-resolution image
        const response = await fetch(`${baseUrl}/files/${fileId}/content`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to load original image');
        }
        
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
        setLoading(false);
      } catch (err) {
        console.error('Error loading original image:', err);
        // Fall back to thumbnail if available
        if (thumbnail && mimeType) {
          setImageSrc(`data:${mimeType};base64,${thumbnail}`);
        } else {
          setError(true);
        }
        setLoading(false);
      }
    };

    loadImage();
    
    // Clean up object URL on unmount
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileId, thumbnail, mimeType]);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 200,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !imageSrc) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 200,
          bgcolor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: 2,
          border: '2px dashed #ccc',
        }}
        onClick={onClick}
      >
        <Stack alignItems="center" spacing={1}>
          <PhotoCameraIcon sx={{ fontSize: 48, color: 'grey.400' }} />
          <Typography variant="caption" color="textSecondary">
            Click to view image
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover .image-overlay': {
          opacity: 1,
        },
      }}
      onClick={onClick}
    >
      <img
        src={imageSrc}
        alt={originalName || `Image ${fileId}`}
        style={{ 
          width: '100%',
          height: 200,
          objectFit: 'cover',
          display: 'block',
        }}
        onError={() => setError(true)}
      />
      
      {/* Hover overlay */}
      <Box
        className="image-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 24, color: 'text.primary' }} />
          </Box>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'white',
              color: 'text.primary',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 500,
            }}
          >
            Click to view full size
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

interface CompletedForm {
  id: number;
  userId: number;
  formId: number;
  formDefinition: any;
  startedAt: string;
  completedAt: string;
  files?: any[];
  pdfFileId?: number;
  pdfFile?: {
    id: number;
    name: string;
    size?: number;
    createdAt?: string;
  };
  form?: {
    name: string;
  };
  user?: {
    name: string;
    email: string;
  };
  equipment?: {
    id: number;
    name: string;
    make: string;
    model: string;
    manufacturedYear: string;
    type: string;
    licensePlate?: string;
    vin?: string;
  };
  operatorName?: string;
  location?: {
    id: number;
    label: string;
    address?: string;
    latitude: number;
    longitude: number;
  };
  customLocationLabel?: string;
  customLocationLatitude?: number;
  customLocationLongitude?: number;
}

const InfoCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
          {icon}
        </Avatar>
        <Typography variant="h6" fontWeight="600">
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const FieldValue = ({ label, value, status }: { label: string; value: any; status?: 'pass' | 'fail' | 'na' }) => {
  const getStatusIcon = () => {
    if (!status) return null;
    switch (status) {
      case 'pass':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'fail':
        return <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'na':
        return <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      default:
        return null;
    }
  };

  const getStatusChip = () => {
    if (!status || typeof value !== 'string') return value;
    
    const isNoDefects = value?.toLowerCase().includes('no defects');
    const isDefectsFound = value?.toLowerCase().includes('defects found') || value?.toLowerCase().includes('needs repair');
    const isNA = value?.toLowerCase() === 'n/a' || value?.toLowerCase() === 'not applicable';
    
    if (isNoDefects) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label={value}
          size="small"
          color="success"
          variant="outlined"
        />
      );
    } else if (isDefectsFound) {
      return (
        <Chip
          icon={<CancelIcon />}
          label={value}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    } else if (isNA) {
      return (
        <Chip
          label={value}
          size="small"
          variant="outlined"
        />
      );
    }
    return value;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        {getStatusChip()}
      </Typography>
    </Box>
  );
};

const CompletedFormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: rawForm, isLoading: loading, error } = useGetOne<CompletedForm>(
    'completed-forms',
    { id: parseInt(id || '0') }
  );
  
  // Modal state - ALL hooks must be declared before any conditional returns
  const [openModal, setOpenModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImageData, setCurrentImageData] = useState<string>('');

  // Parse formDefinition if it's a string
  const form = React.useMemo(() => {
    if (!rawForm) return null;
    const parsedForm = { ...rawForm };
    if (typeof parsedForm.formDefinition === 'string') {
      try {
        parsedForm.formDefinition = JSON.parse(parsedForm.formDefinition);
      } catch (e) {
        console.error('Failed to parse formDefinition:', e);
      }
    }
    return parsedForm;
  }, [rawForm]);

  // Group form fields by category - MUST be before conditional returns
  const groupedFields = React.useMemo(() => {
    const groups: Record<string, any[]> = {
      'Vehicle Selection': [],
      'General Information': [],
      'Inspection Items': [],
      'Images': [],
      'Signatures': [],
    };

    if (!form || !form.formDefinition?.fields) {
      return groups;
    }

    form.formDefinition.fields.forEach((field: any) => {
      // Skip fields with no value or label
      if (!field.label || (field.userValue === undefined && field.userValue === null)) {
        return;
      }

      if (field.label === "Operator's Name:" || field.label === "Operator's Name") {
        return;
      }

      // Categorize by field type first
      if (field.type === 'Images') {
        groups['Images'].push(field);
      } else if (field.type === 'SignatureImage') {
        groups['Signatures'].push(field);
      } else if (field.type === 'EquipmentLookup') {
        groups['Vehicle Selection'].push(field);
      } else if (field.type === 'Information') {
        // Skip information fields
      } else {
        // For other fields, check if it's an inspection item
        // Inspection items typically have options like "No defects", "Minor defects", "Major defects"
        const hasDefectsInOptions = field.options && Array.isArray(field.options) && 
          field.options.some((opt: string) => opt.toLowerCase().includes('defect'));
        
        const hasDefectsInValue = field.userValue && typeof field.userValue === 'string' &&
          field.userValue.toLowerCase().includes('defect');
        
        // More specific inspection-related keywords
        const inspectionKeywords = [
          'brake', 'tire', 'wheel', 'suspension', 'steering',
          'exhaust', 'horn', 'lamp', 'reflector', 'windshield',
          'wiper', 'washer', 'coupling', 'cargo', 'securement',
          'dangerous goods', 'emergency equipment', 'safety device',
          'fuel system', 'electric', 'hydraulic', 'glass', 'mirror',
          'heater', 'defroster', 'cab', 'driver seat', 'driver controls',
          'defects repaired'
        ];
        
        const labelLower = field.label.toLowerCase();
        const isInspectionItem = hasDefectsInOptions || hasDefectsInValue ||
          inspectionKeywords.some(keyword => labelLower.includes(keyword));
        
        if (isInspectionItem) {
          groups['Inspection Items'].push(field);
        } else {
          // General fields like date, time, location, names, notes
          groups['General Information'].push(field);
        }
      }
    });

    return groups;
  }, [form]);

  const getBase64Image = async (response: Response) => {
    const blob = await response.blob();
    const reader = new FileReader();
    
    await new Promise((resolve, reject) => {
      reader.onload = resolve;
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return reader.result as string;
  };

  // Load image when modal opens or index changes
  useEffect(() => {
    if (openModal && selectedImages[currentImageIndex]) {
      setCurrentImageData(''); // Clear previous image
      const imageUrl = `${baseUrl}/files/${selectedImages[currentImageIndex].id}/content`;
      
      fetch(imageUrl, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return getBase64Image(response);
        })
        .then((imgString) => {
          setCurrentImageData(imgString);
        })
        .catch((err) => console.error('Error loading image:', err));
    }
  }, [currentImageIndex, openModal, selectedImages]);

  const handleImageClick = (images: any[], index: number) => {
    setSelectedImages(images);
    setCurrentImageIndex(index);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentImageData('');
  };

  const handleNext = () => {
    setCurrentImageIndex((currentImageIndex + 1) % selectedImages.length);
  };

  const handlePrev = () => {
    setCurrentImageIndex((currentImageIndex - 1 + selectedImages.length) % selectedImages.length);
  };

  const renderFieldValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderImages = (field: any) => {
    if (!field.userValue) return null;

    let imageIds: number[] = [];
    try {
      imageIds = typeof field.userValue === 'string' 
        ? JSON.parse(field.userValue) 
        : field.userValue;
      
      if (!Array.isArray(imageIds)) {
        imageIds = [imageIds];
      }
    } catch (e) {
      console.error('Error parsing image IDs:', e);
      return <Typography color="textSecondary">Unable to display images</Typography>;
    }

    if (imageIds.length === 0) {
      return <Typography color="textSecondary">No images uploaded</Typography>;
    }

    // Get the file objects for all images
    const imageFiles = imageIds.map(fileId => 
      form?.files?.find((f: any) => f.id === fileId) || { id: fileId }
    ).filter(Boolean);

    return (
      <ImageList sx={{ width: '100%', height: 'auto' }} cols={3} rowHeight={200} gap={12}>
        {imageFiles.map((file, index) => {
          // Always use the source image for better quality
          const imageUrl = `${baseUrl}/files/${file.id}/content`;
          
          return (
            <ImageListItem key={file.id}>
              <ImageThumbnail
                fileId={file.id}
                thumbnail={file.thumbnail}
                mimeType={file.mimeType}
                originalName={file.originalName}
                onClick={() => handleImageClick(imageFiles, index)}
              />
            </ImageListItem>
          );
        })}
      </ImageList>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: '16px 24px' }}>
        <Alert severity="error">{error.message || 'An error occurred'}</Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ padding: '16px 24px' }}>
        <Alert severity="warning">Form not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '24px' }}>
      {/* Breadcrumbs */}
      <ResourceBreadcrumbs 
        resource="completed-forms"
        customNames={{
          singular: form.form?.name || 'Completed Form',
          plural: 'Completed Forms'
        }}
      />

      {/* PDF banner — the primary action on this page, so it gets top billing
          rather than being tucked into a corner of the Form Information card */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          p: 2.5,
          mb: 3,
          mt: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'success.main',
          backgroundColor: 'success.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
            <PictureAsPdfIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Completed Form PDF
            </Typography>
            {form.pdfFile && (
              <Typography variant="body2" color="text.secondary">
                {form.pdfFile.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={form.pdfFileId ? <PictureAsPdfIcon /> : <GetAppIcon />}
          onClick={() => handleDownloadPdf(form.id, form.pdfFileId)}
        >
          Download PDF
        </Button>
      </Paper>

      {/* Info Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Form Information */}
        <Grid item xs={12} md={6}>
          <InfoCard icon={<AssignmentIcon fontSize="small" />} title="Form Information">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FieldValue label="Form Type" value={form.form?.name || 'Unknown Form'} />
                <FieldValue label="Form ID" value={`#${form.id}`} />
                <FieldValue label="Started" value={new Date(form.startedAt).toLocaleString()} />
                <FieldValue label="Completed" value={new Date(form.completedAt).toLocaleString()} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldValue label="Completed By" value={form.user?.name || 'N/A'} />
                <FieldValue label="Email" value={form.user?.email || 'N/A'} />
                {form.operatorName && (
                  <FieldValue label="Operator's Name" value={form.operatorName} />
                )}
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>

        {/* Vehicle/Equipment */}
        {form.equipment && (
          <Grid item xs={12} md={6}>
            <InfoCard icon={<DirectionsCarIcon fontSize="small" />} title="Vehicle/Equipment">
              <FieldValue 
                label="Vehicle" 
                value={`${form.equipment.manufacturedYear || ''} ${form.equipment.make || ''} ${form.equipment.model || ''}`.trim() || form.equipment.name} 
              />
              <FieldValue label="Type" value={form.equipment.type || 'N/A'} />
              {form.equipment.licensePlate && (
                <FieldValue label="License" value={form.equipment.licensePlate} />
              )}
              {form.equipment.vin && (
                <FieldValue label="VIN" value={form.equipment.vin} />
              )}
            </InfoCard>
          </Grid>
        )}

        {/* Location */}
        {(form.location || form.customLocationLabel) && (
          <Grid item xs={12} md={6}>
            <InfoCard icon={<LocationOnIcon fontSize="small" />} title="Location">
              {form.location ? (
                <>
                  <FieldValue label="Location" value={form.location.label} />
                  {form.location.address && (
                    <FieldValue label="Address" value={form.location.address} />
                  )}
                  <FieldValue
                    label="Coordinates"
                    value={
                      <Link
                        href={`https://www.google.com/maps?q=${form.location.latitude},${form.location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {form.location.latitude}, {form.location.longitude}
                      </Link>
                    }
                  />
                </>
              ) : (
                <>
                  <FieldValue label="Location" value={form.customLocationLabel} />
                  {form.customLocationLatitude != null && form.customLocationLongitude != null && (
                    <FieldValue
                      label="Coordinates"
                      value={
                        <Link
                          href={`https://www.google.com/maps?q=${form.customLocationLatitude},${form.customLocationLongitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {form.customLocationLatitude}, {form.customLocationLongitude}
                        </Link>
                      }
                    />
                  )}
                </>
              )}
            </InfoCard>
          </Grid>
        )}
      </Grid>

      {/* Form Data */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
          Form Data
        </Typography>

        {/* Vehicle Selection */}
        {groupedFields['Vehicle Selection'].length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
              Vehicle Selection
            </Typography>
            <Grid container spacing={3}>
              {groupedFields['Vehicle Selection'].map((field: any, index: number) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <FieldValue 
                      label={field.label} 
                      value={renderFieldValue(field.userValue)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* General Information */}
        {groupedFields['General Information'].length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
              General Information
            </Typography>
            <Grid container spacing={3}>
              {groupedFields['General Information'].map((field: any, index: number) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <FieldValue 
                    label={field.label} 
                    value={renderFieldValue(field.userValue)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Inspection Items */}
        {groupedFields['Inspection Items'].length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
              Inspection Items
            </Typography>
            <Grid container spacing={2}>
              {groupedFields['Inspection Items'].map((field: any, index: number) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <FieldValue 
                      label={field.label} 
                      value={renderFieldValue(field.userValue)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Images */}
        {groupedFields['Images'].length > 0 && groupedFields['Images'].map((field: any, index: number) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PhotoCameraIcon color="primary" />
              <Typography variant="h6" fontWeight="500">
                {field.label || 'Photos'}
              </Typography>
            </Box>
            {renderImages(field)}
          </Box>
        ))}

        {/* Signatures */}
        {groupedFields['Signatures'].length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
              Signatures
            </Typography>
            <Grid container spacing={3}>
              {groupedFields['Signatures'].map((field: any, index: number) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {field.label}
                    </Typography>
                    {field.userValue && (
                      <Box sx={{ mt: 1 }}>
                        <img 
                          src={field.userValue.startsWith('data:') ? field.userValue : `data:image/jpeg;base64,${field.userValue}`} 
                          alt={field.label}
                          style={{ 
                            maxWidth: '100%', 
                            height: 'auto',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Image Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullScreen>
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            backgroundColor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selectedImages.length > 1 && (
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 20,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          {currentImageData ? (
            <img
              src={currentImageData}
              alt="Full size"
              style={{ 
                maxWidth: '100%', 
                maxHeight: 'calc(100vh - 64px)',
                objectFit: 'contain' 
              }}
            />
          ) : (
            <CircularProgress sx={{ color: 'white' }} />
          )}

          {selectedImages.length > 1 && (
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 20,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompletedFormView;